import { useState, useEffect, useCallback } from "react"
import "katex/dist/katex.min.css"
import ReactMarkdown from "react-markdown"
import remarkMath from "remark-math"
import rehypeKatex from "rehype-katex"
import { LEVELS, getServerUrl } from "./utils.js"
import "./app.css"

const ALL_LEVELS = Object.values(LEVELS).flatMap((group) => Object.keys(group))

async function postSSE(endpoint, { onResult, onError }) {
  const res = await fetch(getServerUrl(endpoint), { method: "POST" })
  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ""
  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split("\n")
    buffer = lines.pop()
    let eventType = null
    for (const line of lines) {
      if (line.startsWith("event: ")) {
        eventType = line.slice(7).trim()
      } else if (line.startsWith("data: ") && eventType) {
        const payload = JSON.parse(line.slice(6))
        if (eventType === "result") onResult?.(payload)
        else if (eventType === "error") onError?.(payload)
        eventType = null
      }
    }
  }
}

function CaseForm({ form, setForm, onSubmit, onCancel, submitLabel, className, autoFocus }) {
  return (
    <div className={className}>
      <label>
        Equation
        <input
          value={form.equation}
          onChange={(e) => setForm({ ...form, equation: e.target.value })}
          placeholder="x**2 - 5*x + 6 = 0"
          autoFocus={autoFocus}
        />
      </label>
      <label>
        Level
        <select value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })}>
          {ALL_LEVELS.map((l) => (
            <option key={l} value={l}>
              {l}
            </option>
          ))}
        </select>
      </label>
      <label>
        Expected answer
        <input
          value={form.expected_answer}
          onChange={(e) => setForm({ ...form, expected_answer: e.target.value })}
          placeholder="x = 2, x = 3"
        />
      </label>
      <label>
        Notes
        <textarea
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          rows={2}
          placeholder="Optional notes..."
        />
      </label>
      <div style={{ display: "flex", gap: 8 }}>
        <button className="golden-btn" onClick={onSubmit}>
          {submitLabel}
        </button>
        <button className="golden-btn golden-btn-dim" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  )
}

function Badge({ status }) {
  const colors = { pass: "#28aa50", fail: "#cc4444", unverified: "#888" }
  return (
    <span className="golden-badge" style={{ background: colors[status] || "#555" }}>
      {status?.toUpperCase() || "—"}
    </span>
  )
}

function JudgeBadge({ judge }) {
  if (!judge) return null
  const color = judge.verdict === "pass" ? "#2288aa" : "#cc8844"
  return (
    <span className="golden-badge" style={{ background: color }} title={judge.reasoning}>
      JUDGE: {judge.verdict?.toUpperCase()}
    </span>
  )
}

function answerToString(value) {
  if (value == null) return ""
  if (typeof value === "string") return value
  if (typeof value === "object") {
    if (typeof value.symbolic === "string") return value.symbolic
    try {
      return JSON.stringify(value)
    } catch {
      return ""
    }
  }
  return String(value)
}

function shortModelName(model) {
  if (!model) return "—"
  const slash = model.lastIndexOf("/")
  return slash >= 0 ? model.slice(slash + 1) : model
}

function RunRow({ run }) {
  const ts = new Date(run.timestamp).toLocaleString()
  const answerDisplay = run.answer_latex || answerToString(run.answer)
  const inputTokens = run.input_tokens
  const outputTokens = run.output_tokens
  const tokenDisplay = inputTokens != null || outputTokens != null ? `${inputTokens ?? 0}/${outputTokens ?? 0}` : "—"
  const tokenTitle =
    inputTokens != null || outputTokens != null ? `${inputTokens ?? 0} input + ${outputTokens ?? 0} output` : ""
  return (
    <tr className="golden-run-row">
      <td>{ts}</td>
      <td
        style={{
          maxWidth: 200,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={answerDisplay}
      >
        {answerDisplay || "—"}
      </td>
      <td>{run.verified === true ? "yes" : run.verified === false ? "no" : "—"}</td>
      <td>
        <Badge status={run.status} />
      </td>
      <td>{run.steps_count}</td>
      <td>{(run.duration_ms / 1000).toFixed(1)}s</td>
      <td title={run.model || ""}>{shortModelName(run.model)}</td>
      <td>{run.strategy || "—"}</td>
      <td title={tokenTitle}>{tokenDisplay}</td>
      <td>
        <JudgeBadge judge={run.judge} />
      </td>
    </tr>
  )
}

function formatResult(result) {
  if (result == null) return ""
  if (typeof result === "string") return result
  if (typeof result === "object") {
    // Filter out internal keys like _resolved_input, _display, save_vars
    const clean = Object.fromEntries(Object.entries(result).filter(([k]) => !k.startsWith("_") && k !== "save_vars"))
    if (clean.result !== undefined) return String(clean.result)
    const vals = Object.values(clean)
    if (vals.length === 1) return String(vals[0])
    return JSON.stringify(clean, null, 0)
  }
  return String(result)
}

function formatInput(input) {
  if (!input || typeof input !== "object") return ""
  return Object.entries(input)
    .filter(([k]) => !k.startsWith("_"))
    .map(([k, v]) => `${k}=${JSON.stringify(v)}`)
    .join(", ")
}

function TraceSteps({ caseId, run, onStepNoteChange }) {
  const [showTrace, setShowTrace] = useState(true)
  const trace = run?.trace || []
  const steps = run?.steps || []
  const stepNotes = run?.step_notes || {}
  const judgeStepNotes = run?.judge?.step_notes || {}
  const judgeReasoning = run?.judge?.reasoning

  if (!trace.length && !steps.length) return null

  const hasTrace = trace.length > 0
  const rows = hasTrace ? trace : steps
  const rowCount = rows.length

  return (
    <div className="golden-trace">
      {judgeReasoning && (
        <div className="golden-judge-summary">
          <span className="golden-judge-icon">⚖</span> {judgeReasoning}
        </div>
      )}
      <div
        className="golden-label"
        style={{ cursor: "pointer", marginBottom: 6, userSelect: "none" }}
        onClick={() => setShowTrace(!showTrace)}
      >
        <span style={{ fontSize: 10, marginRight: 6 }}>{showTrace ? "▼" : "▶"}</span>
        Steps ({rowCount})
      </div>
      {showTrace && (
        <div className="golden-trace-list">
          {rows.map((row, i) => {
            const explanation = hasTrace ? steps[i]?.explanation || "" : row?.explanation || ""
            const judgeNote = judgeStepNotes[String(i)] || ""
            return (
              <div key={i} className="golden-trace-step">
                {hasTrace && (
                  <div className="golden-trace-step-header">
                    <span className="golden-trace-num">{i + 1}</span>
                    <code className="golden-trace-call">
                      {row.action}({formatInput(row.input)})
                    </code>
                    <span className="golden-trace-result" title={formatResult(row.result)}>
                      = {formatResult(row.result)}
                    </span>
                  </div>
                )}
                {!hasTrace && (
                  <div className="golden-trace-step-header">
                    <span className="golden-trace-num">{i + 1}</span>
                  </div>
                )}
                {explanation && (
                  <div className="golden-trace-explanation">
                    <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                      {explanation}
                    </ReactMarkdown>
                  </div>
                )}
                {hasTrace && !explanation && row.why && <div className="golden-trace-why">{row.why}</div>}
                {judgeNote && (
                  <div className="golden-judge-note">
                    <span className="golden-judge-icon">⚖</span> {judgeNote}
                  </div>
                )}
                <textarea
                  className="golden-step-note"
                  placeholder="Add note..."
                  defaultValue={stepNotes[String(i)] || ""}
                  onBlur={(e) => onStepNoteChange(caseId, run.id, i, e.target.value)}
                  rows={1}
                />
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function CaseCard({ c, onUpdate, onDelete, onRun, onJudge, onRunAndJudge, onStepNote, runningId, judgingId }) {
  const [expanded, setExpanded] = useState(false)
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState({
    equation: c.equation,
    level: c.level,
    expected_answer: c.expected_answer,
    notes: c.notes,
  })
  const [notesLocal, setNotesLocal] = useState(c.notes)
  const isRunning = runningId === c.id
  const isJudging = judgingId === c.id
  const lastRun = c.runs?.[0]

  useEffect(() => {
    setNotesLocal(c.notes)
    setForm({
      equation: c.equation,
      level: c.level,
      expected_answer: c.expected_answer,
      notes: c.notes,
    })
  }, [c])

  const saveNotes = () => {
    if (notesLocal !== c.notes) {
      onUpdate(c.id, { notes: notesLocal })
    }
  }

  const saveEdit = () => {
    onUpdate(c.id, form)
    setEditing(false)
  }

  return (
    <div className="golden-case">
      <div className="golden-case-header" onClick={() => setExpanded(!expanded)}>
        <span className="golden-arrow">{expanded ? "▼" : "▶"}</span>
        <code className="golden-eq" onClick={(event) => event.stopPropagation()}>
          {c.equation}
        </code>
        <span className="golden-header-spacer" />
        <span className="golden-level-badge">{c.level}</span>
        {lastRun && <Badge status={lastRun.status} />}
        {lastRun?.judge && <JudgeBadge judge={lastRun.judge} />}
        {(isRunning || isJudging) && (
          <span className="golden-badge" style={{ background: "#4444cc" }}>
            {isRunning ? "RUNNING…" : "JUDGING…"}
          </span>
        )}
      </div>

      {expanded && (
        <div className="golden-case-body">
          {editing ? (
            <CaseForm
              form={form}
              setForm={setForm}
              onSubmit={saveEdit}
              onCancel={() => setEditing(false)}
              submitLabel="Save"
              className="golden-edit-form"
            />
          ) : (
            <>
              <div className="golden-field">
                <span className="golden-label">Expected:</span> {c.expected_answer || "—"}
              </div>
              <div className="golden-field">
                <span className="golden-label">Notes:</span>
                <textarea
                  className="golden-notes"
                  value={notesLocal}
                  onChange={(e) => setNotesLocal(e.target.value)}
                  onBlur={saveNotes}
                  rows={2}
                  placeholder="Add notes..."
                />
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button className="golden-btn" onClick={() => onRun(c.id)} disabled={isRunning}>
                  {isRunning ? "running..." : "Run"}
                </button>
                <button
                  className="golden-btn golden-btn-judge"
                  onClick={() => onJudge(c.id)}
                  disabled={isJudging || (!lastRun?.trace?.length && !lastRun?.steps?.length)}
                >
                  {isJudging ? "judging..." : "Judge"}
                </button>
                <button
                  className="golden-btn golden-btn-action"
                  onClick={() => onRunAndJudge(c.id)}
                  disabled={isRunning || isJudging}
                >
                  {isRunning ? "running..." : isJudging ? "judging..." : "Run + Judge"}
                </button>
                <button className="golden-btn golden-btn-dim" onClick={() => setEditing(true)}>
                  Edit
                </button>
                <button className="golden-btn golden-btn-danger" onClick={() => onDelete(c.id)}>
                  Delete
                </button>
              </div>

              {lastRun && <TraceSteps caseId={c.id} run={lastRun} onStepNoteChange={onStepNote} />}
            </>
          )}

          {c.runs?.length > 0 && (
            <div className="golden-history">
              <div className="golden-label" style={{ marginBottom: 6 }}>
                Run history ({c.runs.length})
              </div>
              <table className="golden-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Answer</th>
                    <th>Verified</th>
                    <th>Status</th>
                    <th>Steps</th>
                    <th>Duration</th>
                    <th>Model</th>
                    <th>Strategy</th>
                    <th title="input / output tokens">Tokens</th>
                    <th>Judge</th>
                  </tr>
                </thead>
                <tbody>
                  {c.runs.map((r) => (
                    <RunRow key={r.id} run={r} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function AddCaseForm({ onAdd }) {
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState({
    equation: "",
    level: "9class",
    expected_answer: "",
    notes: "",
  })

  const submit = () => {
    if (!form.equation.trim()) return
    onAdd(form)
    setForm({ equation: "", level: "9class", expected_answer: "", notes: "" })
    setOpen(false)
  }

  if (!open) {
    return (
      <button className="golden-btn" onClick={() => setOpen(true)}>
        + Add Case
      </button>
    )
  }

  return (
    <CaseForm
      form={form}
      setForm={setForm}
      onSubmit={submit}
      onCancel={() => setOpen(false)}
      submitLabel="Create"
      className="golden-add-form"
      autoFocus
    />
  )
}

export default function GoldenPage() {
  const [cases, setCases] = useState([])
  const [runningId, setRunningId] = useState(null)
  const [judgingId, setJudgingId] = useState(null)
  const [runningAll, setRunningAll] = useState(false)
  const [judgingAll, setJudgingAll] = useState(false)
  const [error, setError] = useState(null)
  const [filterStatus, setFilterStatus] = useState("all")
  const [filterJudge, setFilterJudge] = useState("all")
  const [filterLevel, setFilterLevel] = useState("all")
  const [sortBy, setSortBy] = useState("created")

  const STATUS_ORDER = { none: 0, fail: 1, unverified: 2, pass: 3 }
  const JUDGE_ORDER = { none: 0, fail: 1, pass: 2 }

  const filteredCases = cases
    .filter((c) => {
      const lastRun = c.runs?.[0]
      if (filterStatus !== "all") {
        const status = lastRun?.status || "none"
        if (filterStatus === "none" ? lastRun : status !== filterStatus) return false
      }
      if (filterJudge !== "all") {
        const verdict = lastRun?.judge?.verdict || "none"
        if (filterJudge === "none" ? lastRun?.judge : verdict !== filterJudge) return false
      }
      if (filterLevel !== "all" && c.level !== filterLevel) return false
      return true
    })
    .sort((a, b) => {
      if (sortBy === "status") {
        const statusA = a.runs?.[0]?.status || "none"
        const statusB = b.runs?.[0]?.status || "none"
        return (STATUS_ORDER[statusA] ?? 3) - (STATUS_ORDER[statusB] ?? 3)
      }
      if (sortBy === "judge") {
        const judgeA = a.runs?.[0]?.judge?.verdict || "none"
        const judgeB = b.runs?.[0]?.judge?.verdict || "none"
        return (JUDGE_ORDER[judgeA] ?? 2) - (JUDGE_ORDER[judgeB] ?? 2)
      }
      return 0
    })

  const fetchCases = useCallback(async () => {
    try {
      const res = await fetch(getServerUrl("api/golden"))
      const data = await res.json()
      setCases(data.cases || [])
    } catch (e) {
      setError("Failed to load cases")
    }
  }, [])

  useEffect(() => {
    fetchCases()
  }, [fetchCases])

  const addCase = async (form) => {
    await fetch(getServerUrl("api/golden"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    })
    fetchCases()
  }

  const updateCase = async (id, fields) => {
    await fetch(getServerUrl(`api/golden/${id}`), {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(fields),
    })
    fetchCases()
  }

  const deleteCase = async (id) => {
    await fetch(getServerUrl(`api/golden/${id}`), { method: "DELETE" })
    fetchCases()
  }

  const saveStepNote = async (caseId, runId, stepIndex, note) => {
    await fetch(getServerUrl(`api/golden/${caseId}/runs/${runId}/steps/${stepIndex}/note`), {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ note }),
    })
  }

  const runOne = async (id) => {
    setRunningId(id)
    try {
      await fetch(getServerUrl(`api/golden/${id}/run`), { method: "POST" })
      fetchCases()
    } finally {
      setRunningId(null)
    }
  }

  const runAndJudge = async (id) => {
    setRunningId(id)
    try {
      await fetch(getServerUrl(`api/golden/${id}/run`), { method: "POST" })
      await fetchCases()
    } finally {
      setRunningId(null)
    }
    setJudgingId(id)
    try {
      const res = await fetch(getServerUrl(`api/golden/${id}/judge`), { method: "POST" })
      if (res.ok) {
        const payload = await res.json()
        setCases((prev) =>
          prev.map((c) => {
            if (c.id !== payload.case_id) return c
            const runs = c.runs.map((r) => (r.id === payload.run_id ? { ...r, judge: payload.judge } : r))
            return { ...c, runs }
          })
        )
      } else {
        const err = await res.json().catch(() => ({}))
        setError(err.detail || "Judge failed")
      }
    } finally {
      setJudgingId(null)
    }
  }

  const judgeOne = async (id) => {
    setJudgingId(id)
    try {
      const res = await fetch(getServerUrl(`api/golden/${id}/judge`), { method: "POST" })
      if (res.ok) {
        const payload = await res.json()
        setCases((prev) =>
          prev.map((c) => {
            if (c.id !== payload.case_id) return c
            const runs = c.runs.map((r) => (r.id === payload.run_id ? { ...r, judge: payload.judge } : r))
            return { ...c, runs }
          })
        )
      } else {
        const err = await res.json().catch(() => ({}))
        setError(err.detail || "Judge failed")
      }
    } finally {
      setJudgingId(null)
    }
  }

  const runAllSSE = async () => {
    setRunningAll(true)
    try {
      await postSSE("api/golden/run-all", {
        onResult: (payload) => {
          setCases((prev) => prev.map((c) => (c.id === payload.case_id ? { ...c, runs: [payload.run, ...c.runs] } : c)))
        },
        onError: (payload) => setError(`Error on case: ${payload.message}`),
      })
    } catch {
      setError("Connection lost during run-all")
    } finally {
      setRunningAll(false)
    }
  }

  const judgeAllSSE = async () => {
    setJudgingAll(true)
    try {
      await postSSE("api/golden/judge-all", {
        onResult: (payload) => {
          setCases((prev) =>
            prev.map((c) => {
              if (c.id !== payload.case_id) return c
              const runs = c.runs.map((r) => (r.id === payload.run_id ? { ...r, judge: payload.judge } : r))
              return { ...c, runs }
            })
          )
        },
        onError: (payload) => setError(`Judge error: ${payload.message}`),
      })
    } catch {
      setError("Connection lost during judge-all")
    } finally {
      setJudgingAll(false)
    }
  }

  return (
    <div
      style={{
        height: "100vh",
        overflowY: "auto",
        background: "#08080d",
        color: "#d0d0e0",
        fontFamily: "monospace",
        padding: "24px",
        maxWidth: 900,
        margin: "0 auto",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 20,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <h1 style={{ fontSize: 18, fontWeight: 600, color: "#fff" }}>golden cases</h1>
          <a href="#/" style={{ color: "#6868cc", fontSize: 13, textDecoration: "none" }}>
            ← solver
          </a>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="golden-btn golden-btn-action" onClick={runAllSSE} disabled={runningAll || judgingAll}>
            {runningAll ? "recomputing..." : "Recompute All"}
          </button>
          <button className="golden-btn golden-btn-judge" onClick={judgeAllSSE} disabled={judgingAll || runningAll}>
            {judgingAll ? "judging..." : "Verify by Judge"}
          </button>
        </div>
      </div>

      {error && (
        <div
          style={{
            color: "#cc4444",
            padding: "8px 0",
            fontSize: 13,
            marginBottom: 8,
          }}
        >
          {error}
          <span style={{ marginLeft: 12, cursor: "pointer", color: "#888" }} onClick={() => setError(null)}>
            ✕
          </span>
        </div>
      )}

      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12, fontSize: 12, flexWrap: "wrap" }}>
        <label style={{ display: "flex", gap: 4, alignItems: "center" }}>
          Status
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="golden-filter-select"
          >
            <option value="all">All</option>
            <option value="pass">Pass</option>
            <option value="fail">Fail</option>
            <option value="unverified">Unverified</option>
            <option value="none">No runs</option>
          </select>
        </label>
        <label style={{ display: "flex", gap: 4, alignItems: "center" }}>
          Judge
          <select value={filterJudge} onChange={(e) => setFilterJudge(e.target.value)} className="golden-filter-select">
            <option value="all">All</option>
            <option value="pass">Pass</option>
            <option value="fail">Fail</option>
            <option value="none">No judge</option>
          </select>
        </label>
        <label style={{ display: "flex", gap: 4, alignItems: "center" }}>
          Level
          <select value={filterLevel} onChange={(e) => setFilterLevel(e.target.value)} className="golden-filter-select">
            <option value="all">All</option>
            {ALL_LEVELS.map((level) => (
              <option key={level} value={level}>
                {level}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: "flex", gap: 4, alignItems: "center" }}>
          Sort
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="golden-filter-select">
            <option value="created">Created</option>
            <option value="status">Status</option>
            <option value="judge">Judge</option>
          </select>
        </label>
        <span style={{ color: "#666" }}>
          {filteredCases.length}/{cases.length} cases
        </span>
      </div>

      <div style={{ marginBottom: 16 }}>
        <AddCaseForm onAdd={addCase} />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {filteredCases.map((c) => (
          <CaseCard
            key={c.id}
            c={c}
            onUpdate={updateCase}
            onDelete={deleteCase}
            onRun={runOne}
            onJudge={judgeOne}
            onRunAndJudge={runAndJudge}
            onStepNote={saveStepNote}
            runningId={runningId}
            judgingId={judgingId}
          />
        ))}
      </div>

      {cases.length === 0 && (
        <div style={{ color: "#555", textAlign: "center", padding: 40 }}>No golden cases yet. Add one above.</div>
      )}
    </div>
  )
}
